import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IQueryParam, IResumenDatosBase } from 'modelos/src';
import {
  subDays,
  subMonths,
  startOfMonth,
  startOfDay,
  endOfDay,
  isSameDay,
  isSameMonth,
} from 'date-fns';
import { ResumenesDatosService } from '../../../../../auxiliares/servicios/http/resumenes-datos.service';
import { HttpClientService } from '../../../../../auxiliares/servicios/http.service';
import { CarruselComponent } from '../../../../../auxiliares/carrusel/carrusel.component';
import { DatePickerComponent } from '../../../../../componentes/date-picker/date-picker.component';
import { LoginService } from '../../../../login/login.service';

type TipoCombustible = 'cargas' | 'sospechosos' | 'mensual';

const TIPO_RESUMEN: Record<TipoCombustible, string> = {
  cargas: 'Informe Cargas Combustible',
  sospechosos: 'Informe Eventos Sospechosos Combustible',
  mensual: 'Informe Mensual Flota Combustible',
};

@Component({
  selector: 'app-informe-combustible',
  standalone: true,
  imports: [TooltipDirective, 
    CommonModule,
    DatePipe,
    FormsModule,
    BOTONES,
    
    MatIconModule,
    MatProgressSpinnerModule,
    
    CarruselComponent,
    DatePickerComponent,
  ],
  templateUrl: './informe-combustible.component.html',
  styleUrl: './informe-combustible.component.scss',
})
export class InformeCombustibleComponent implements OnInit {
  private resumenesService = inject(ResumenesDatosService);
  private http = inject(HttpClientService);

  /** Cuando se pasa, filtra los datos del resumen para ese vehículo */
  @Input() idActivo?: string;

  tipo: TipoCombustible = 'cargas';
  periodo: 7 | 30 = 7;
  // El informe diario cierra a fin de día: hoy siempre sería parcial
  fechaISO: string = subDays(new Date(), 1).toISOString();
  readonly ayer = subDays(new Date(), 1);
  readonly idClientePropio = LoginService.getUsuario()?.idCliente;

  informes: IResumenDatosBase<any>[] = [];
  indiceActual = 0;
  cargando = false;
  generando = false;

  ngOnInit() {
    this.cargarInformes();
  }

  async cargarInformes() {
    this.cargando = true;
    try {
      const fechaSeleccionada = this.fechaISO
        ? new Date(this.fechaISO)
        : new Date();
      // Ventana de `periodo` hacia atrás terminando en la fecha elegida:
      // así el picker realmente navega el carrusel en vez de solo ensanchar
      const hasta = endOfDay(fechaSeleccionada);
      const desde =
        this.tipo === 'mensual'
          ? subMonths(
              startOfDay(fechaSeleccionada),
              this.periodo === 7 ? 3 : 12,
            )
          : subDays(startOfDay(fechaSeleccionada), this.periodo);
      const rango = { $gte: desde.toISOString(), $lte: hasta.toISOString() };

      // Informes por vehículo (Individual) cuando hay idActivo, por cliente (Cliente) en dashboard
      const filtroAgrupacion = this.idActivo
        ? {
            tipo: TIPO_RESUMEN[this.tipo],
            agrupacion: 'Individual',
            idsAsignados: this.idActivo,
            periodoInicio: rango,
          }
        : {
            tipo: TIPO_RESUMEN[this.tipo],
            agrupacion: 'Cliente',
            periodoInicio: rango,
          };

      const query: IQueryParam = {
        filter: JSON.stringify(filtroAgrupacion),
        sort: '-periodoInicio',
        unlimited: true,
        includeChildren: true,
        populate: JSON.stringify([{ path: 'cliente', select: 'nombre' }]),
      };
      const resultado = await this.resumenesService.getFiltered(query);
      this.informes = resultado.datos as any[];
      // Posicionar el carrusel en el informe de la fecha elegida si existe
      const idxFecha = this.informes.findIndex((i) => {
        const inicio = new Date(i.periodoInicio ?? 0);
        return this.tipo === 'mensual'
          ? isSameMonth(inicio, fechaSeleccionada)
          : isSameDay(inicio, fechaSeleccionada);
      });
      this.indiceActual = idxFecha >= 0 ? idxFecha : 0;
    } finally {
      this.cargando = false;
    }
  }

  onIndexChange(idx: number) {
    this.indiceActual = idx;
  }

  toggleTipo(t: TipoCombustible) {
    this.tipo = t;
    this.cargarInformes();
  }

  togglePeriodo(p: 7 | 30) {
    this.periodo = p;
    this.cargarInformes();
  }

  onFechaChange() {
    this.cargarInformes();
  }

  get informeActual(): IResumenDatosBase<any> | undefined {
    return this.informes[this.indiceActual];
  }

  /** El resumen ya viene filtrado desde el servidor (por vehículo o por cliente). */
  get resumenFiltrado(): any {
    return this.informeActual?.resumen;
  }

  /** El informe del cliente propio es la sumatoria de sus descendientes. */
  get esInformePropio(): boolean {
    return this.informeActual?.idCliente === this.idClientePropio;
  }

  async verInforme() {
    const id = this.informeActual?._id;
    if (!id) return;
    const html = await this.http.get<string>(
      `/resumenes-datos/${id}/informe-combustible/html`,
      { responseType: 'text' as any },
    );
    this.abrirHtml(html);
  }

  /** Abre el HTML en una pestaña y libera el blob una vez cargado. */
  private abrirHtml(html: string) {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async generarInforme() {
    this.generando = true;
    try {
      const fecha =
        this.tipo === 'mensual'
          ? startOfMonth(new Date(this.fechaISO)).toISOString()
          : this.fechaISO;

      let url =
        `/resumenes-datos/informe-combustible/html-por-fecha` +
        `?tipo=${this.tipo}&fecha=${encodeURIComponent(fecha)}`;
      if (this.idActivo)
        url += `&idActivo=${encodeURIComponent(this.idActivo)}`;

      const html = await this.http.get<string>(url, {
        responseType: 'text' as any,
      });
      this.abrirHtml(html);
    } finally {
      this.generando = false;
    }
    await this.cargarInformes();
  }
}
